import { Patient } from '@models/patient';
import { Avatar, Button, Flex, Row, Space, Tag, Typography, Modal } from 'antd';
import { getFirstChar } from '../../utils/string';
import {
  FileTextOutlined,
  IdcardOutlined,
  LineChartOutlined,
  MonitorOutlined,
  PictureOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { PatientCareLevel } from '../../constants/careLevel';
import useMobile from '../../hooks/useMobile';
import { IndependentLifeLevel } from '../../constants/independentLifeLevel';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TagEditModal } from './TagEditModal';
import { extractPatientTags } from '../../utils/patient-tags';
import { useTranslation } from 'react-i18next';
import { usePlanGuard } from '../../hooks/usePlanGuard';

export type Props = {
  patient: Patient;
  ohatScoresCount: number;
  onClick: () => {};
  /** タグ更新後などに患者情報を再取得するためのコールバック */
  onUpdated?: () => void;
};
export const PatientDetailPanel = (props: Props) => {
  const { t, i18n } = useTranslation();
  const { patient, ohatScoresCount, onClick, onUpdated } = props;
  const { isMobile } = useMobile();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const navigate = useNavigate();
  const { guard } = usePlanGuard();
  const isArchived = !!patient.archivedAt;

  const currentTags = extractPatientTags(patient);

  const lifeLevel = IndependentLifeLevel.getByKey(patient.independentLifeLevel);
  const careLevel = PatientCareLevel.getByKey(patient.careLevel);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onClickEdit = () => {
    navigate(`edit`);
  };
  return (
    <Flex
      vertical
      align="center"
      gap={isMobile() ? 'small' : 'middle'}
      justify="start"
    >
      <Avatar size={{ xs: 60, sm: 60, md: 80, lg: 80, xl: 80, xxl: 80 }}>
        {getFirstChar(patient.name ?? '')}
      </Avatar>
      <Typography.Text style={{ fontSize: 20, fontWeight: 'bold' }}>
        {patient.age
          ? `${patient.name} (${t('patient.ageValue', { age: patient.age })})`
          : `${patient.name}`}
      </Typography.Text>
      {isArchived && <Tag color="default">{t('patient.archivedBadge')}</Tag>}
      <Flex vertical align="center" style={{ width: '100%' }}>
        <Row style={{ color: 'gray' }}>
          <LineChartOutlined />
          <Typography.Text style={{ marginLeft: 8 }}>
            {t('patient.assessmentCount', { count: ohatScoresCount })}
          </Typography.Text>
        </Row>
        <Row style={{ color: 'gray' }}>
          <IdcardOutlined />
          <Typography.Text style={{ marginLeft: 8 }}>
            {`${
              lifeLevel === IndependentLifeLevel.none
                ? t('patient.lifeLevelNone')
                : lifeLevel.getLabel(i18n.language)
            } ${
              careLevel === PatientCareLevel.none
                ? t('patient.careLevelNone')
                : careLevel.getLabel(i18n.language)
            } `}
          </Typography.Text>
        </Row>
        {currentTags.length > 0 && (
          <Row style={{ width: '100%', marginTop: 6 }}>
            <Space size={[8, 4]} wrap>
              {currentTags.map((t) => (
                <Tag key={t.id} color="cyan" icon={<TagsOutlined />}>
                  {t.name}
                </Tag>
              ))}
            </Space>
          </Row>
        )}
        {patient.memo && (
          <>
            <Row style={{ color: 'gray' }}>
              <FileTextOutlined />
              <Typography.Text style={{ marginLeft: 8 }}>
                {t('patient.memo')}
              </Typography.Text>
            </Row>
            <Row style={{ color: 'gray', width: '100%', marginTop: 3 }}>
              <Flex
                flex={1}
                vertical
                style={{
                  width: '100%',
                  backgroundColor: '#f5f5f5',
                  padding: 6,
                  borderRadius: 8,
                  border: '1px solid #d9d9d9',
                }}
              >
                <Typography.Paragraph
                  ellipsis={{ rows: 3 }}
                  style={{
                    width: '100%',
                    lineBreak: 'auto',
                    marginBottom: 0,
                    fontSize: 12,
                  }}
                >
                  {patient.memo}
                </Typography.Paragraph>
                <Button
                  type="link"
                  onClick={showModal}
                  style={{ marginTop: 0, fontSize: 12, paddingBottom: 0 }}
                >
                  {t('common.showMore')}
                </Button>
              </Flex>
            </Row>
          </>
        )}
      </Flex>
      <Flex
        vertical
        gap="small"
        style={{ width: '100%', marginTop: isMobile() ? 4 : 8 }}
      >
        {/* 患者メモとタグ編集は「患者情報の編集」グループとして横並びにまとめる */}
        <Flex gap="small" wrap="wrap" justify="center">
          <Button
            shape="round"
            type="default"
            icon={<FileTextOutlined />}
            onClick={onClickEdit}
            style={{ flex: 1, minWidth: 130 }}
          >
            {patient.memo ? t('patient.memoEdit') : t('patient.memoAdd')}
          </Button>
          <Button
            shape="round"
            type="default"
            icon={<TagsOutlined />}
            onClick={() =>
              guard('folder_tag', () => setIsTagModalOpen(true))
            }
            style={{ flex: 1, minWidth: 130 }}
          >
            {t('patient.editTags')}
          </Button>
        </Flex>
        <Button
          shape="round"
          type="default"
          block
          icon={<PictureOutlined />}
          onClick={() => navigate('photos')}
        >
          {t('patient.uploadedPhotos')}
        </Button>
        <Button
          shape="round"
          size="large"
          block
          type="primary"
          onClick={onClick}
          icon={<MonitorOutlined />}
        >
          {t('patient.ohatAssess')}
        </Button>
      </Flex>

      <Modal
        title={t('patient.memo')}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={false}
      >
        <Typography.Paragraph
          style={{
            whiteSpace: 'pre-wrap',
            height: '60vh',
            overflowY: 'auto',
          }}
        >
          {patient.memo}
        </Typography.Paragraph>
      </Modal>

      <TagEditModal
        open={isTagModalOpen}
        patientId={patient.id}
        currentTags={currentTags}
        onClose={() => setIsTagModalOpen(false)}
        onSaved={() => onUpdated?.()}
      />
    </Flex>
  );
};
